'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FeedAuthor } from '@/platform/feed/FeedAuthor';
import { excerpt } from '@/platform/feed/feedUtils';
import { getForumPost, type ForumPost } from './api';
import { FeedDiscussionAccordion } from './FeedDiscussionAccordion';
import { ForumPostMedia } from './ForumPostMedia';
import { PostActions } from './PostActions';

type Props = {
  post: ForumPost;
  disableNavigation?: boolean;
  hrefOverride?: string;
  authorFixture?: { displayName?: string; handle?: string; avatarUrl?: string };
  /** In-app select (product tour) — no URL change. */
  onSelect?: (postId: string) => void;
  onSelectAuthor?: (userId: string) => void;
  tourMode?: boolean;
  /** Parent board updates list row (e.g. replyCount / watching). */
  onPostChange?: (post: ForumPost) => void;
};

export function ForumPostFeedCard({
  post,
  disableNavigation = false,
  hrefOverride,
  authorFixture,
  onSelect,
  onSelectAuthor,
  tourMode = false,
  onPostChange,
}: Props) {
  const [localPost, setLocalPost] = useState(post);
  const href = hrefOverride ?? `/forum/posts/${post.id}/`;
  const linked = !disableNavigation && !onSelect;

  useEffect(() => {
    setLocalPost(post);
  }, [post]);

  function applyPost(next: ForumPost) {
    setLocalPost(next);
    onPostChange?.(next);
  }

  async function refreshPost() {
    if (tourMode) return;
    try {
      const fresh = await getForumPost(localPost.id);
      applyPost(fresh);
    } catch {
      /* keep local */
    }
  }

  return (
    <article className="feed-card feed-card--social">
      <FeedAuthor
        userId={localPost.authorId}
        createdAt={localPost.createdAt}
        meta="Discussion"
        fixtureProfile={authorFixture}
        disableProfileLink={!onSelectAuthor && (!!authorFixture || disableNavigation || !!onSelect)}
        onSelectAuthor={onSelectAuthor}
      />
      <div className="feed-card__body">
        <h3 className="feed-card__title">
          {linked ? (
            <Link href={href}>{localPost.title}</Link>
          ) : onSelect ? (
            <button type="button" className="tour-text-btn" onClick={() => onSelect(localPost.id)}>
              {localPost.title}
            </button>
          ) : (
            localPost.title
          )}
        </h3>
        {localPost.body && <p className="feed-card__excerpt">{excerpt(localPost.body)}</p>}
        <ForumPostMedia
          imageUrls={localPost.imageUrls}
          youtubeVideoId={localPost.youtubeVideoId}
          title={localPost.title}
        />
      </div>
      <footer className="feed-card__actions">
        <PostActions post={localPost} onUpdate={() => void refreshPost()} tourMode={tourMode} />
        {linked ? (
          <Link href={href} className="feed-card__cta">
            Open thread
          </Link>
        ) : onSelect ? (
          <button
            type="button"
            className="feed-card__cta tour-text-btn"
            onClick={() => onSelect(localPost.id)}
          >
            Open thread
          </button>
        ) : null}
      </footer>
      <FeedDiscussionAccordion
        post={localPost}
        tourMode={tourMode}
        onReplyCountChange={(replyCount) => {
          if (replyCount === localPost.replyCount) return;
          applyPost({ ...localPost, replyCount });
        }}
      />
    </article>
  );
}
